import { useEffect, useState } from "react";
import { Check, Eye, FilePlus2, RefreshCw, Trash2, Upload, X, PanelLeft } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import Hint from "@/components/Hint";

export const authed = (url) => window.fetch(url, { headers: { Authorization: `Bearer ${localStorage.getItem("auth_token") || ""}` } });
const fmt = (d) => (d ? new Date(d).toLocaleDateString("en-US", { month: "numeric", day: "numeric", year: "2-digit" }) : "");

const FilePick = ({ onFiles, testId, className, children, multiple = false }) => (
  <label className={className}>
    {children}
    <input data-testid={testId} type="file" className="hidden" accept=".pdf,.docx" multiple={multiple} onChange={onFiles} />
  </label>
);

const MenuItem = ({ icon: Icon, children, ...rest }) => (
  <button type="button" className="flex w-full items-center gap-2 px-2.5 py-1.5 text-left text-xs text-slate-600 hover:bg-slate-50" {...rest}>
    <Icon className="h-3.5 w-3.5" /> {children}
  </button>
);

/** Contract / Proposal button: nothing loaded → blue upload; loaded → ✓ + filename (Contract blue, Proposal green; menu: view, replace, remove, versions). */
export function DocButton({ label, type, docs, onUpload, onReplace, onNewReview, onView, onRemove, onMakeActive }) {
  const [open, setOpen] = useState(false);
  // The review's file: the active one, else the latest uploaded (a newer upload elsewhere may have unset is_active).
  const active = docs.find((d) => d.is_active) || docs[docs.length - 1];
  const key = label.toLowerCase();
  if (!active) {
    return (
      <FilePick onFiles={onUpload} testId={`ca-upload-${key}-input`} multiple
                className="inline-flex h-7 cursor-pointer items-center gap-1 rounded-md bg-[#007BFF] px-2.5 text-xs font-medium text-white transition-colors hover:bg-[#0069d9]">
        <Upload className="h-3.5 w-3.5" /> {label}
      </FilePick>
    );
  }
  const older = docs.filter((d) => d.file_id !== active.file_id);
  const close = () => setOpen(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" data-testid={`ca-doc-btn-${key}`} title={active.filename}
                className={`inline-flex h-7 max-w-[11rem] items-center gap-1 rounded-md px-2.5 text-xs font-medium text-white transition-colors ${
                  type === "contract" ? "bg-[#007BFF] hover:bg-[#0069d9]" : "bg-green-600 hover:bg-green-700"}`}>
          <Check className="h-3.5 w-3.5 shrink-0" /> {label} <span className="truncate font-normal opacity-90">· {active.filename}</span>
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-72 p-0" data-testid={`ca-doc-menu-${key}`}>
        <div className="border-b border-slate-100 px-2.5 py-2 text-[11px]">
          <div className="truncate font-medium text-slate-800">{active.filename}</div>
          <div className="text-slate-400">Uploaded {fmt(active.upload_date)} · chat reads every file in this review</div>
        </div>
        <div className="py-1">
          <MenuItem icon={Eye} onClick={() => { close(); onView(active, type); }} data-testid={`ca-doc-view-${key}`}>View</MenuItem>
          <FilePick onFiles={(e) => { close(); onReplace(e); }} testId={`ca-doc-replace-${key}`}
                    className="flex w-full cursor-pointer items-center gap-2 px-2.5 py-1.5 text-xs text-slate-600 hover:bg-slate-50">
            <RefreshCw className="h-3.5 w-3.5" /> Replace (same review)
          </FilePick>
          {onNewReview && (
            <FilePick onFiles={(e) => { close(); onNewReview(e); }} testId={`ca-doc-new-review-${key}`}
                      className="flex w-full cursor-pointer items-center gap-2 px-2.5 py-1.5 text-xs text-slate-600 hover:bg-slate-50">
              <FilePlus2 className="h-3.5 w-3.5" /> New review with another {key}
            </FilePick>)}
          <MenuItem icon={Trash2} onClick={() => { close(); onRemove(active.file_id, type); }} data-testid={`ca-doc-remove-${key}`}>Remove</MenuItem>
        </div>
        {older.length > 0 && (
          <div className="border-t border-slate-100 py-1">
            <div className="px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wide text-slate-400">Older versions</div>
            {older.map((d) => (
              <div key={d.file_id} className="flex items-center gap-2 px-2.5 py-1 text-xs text-slate-500" data-testid={`ca-doc-${d.file_id}`}>
                <span className="min-w-0 flex-1 truncate" title={d.filename}>{d.filename} <span className="text-[10px] text-slate-400">{fmt(d.upload_date)}</span></span>
                <button type="button" title="View" onClick={() => { close(); onView(d, type); }} className="p-0.5 hover:text-slate-800"><Eye className="h-3 w-3" /></button>
                <button type="button" onClick={() => onMakeActive(d.file_id, type)} data-testid={`ca-set-active-${type}-${d.file_id}`}
                        className="rounded bg-slate-200 px-1.5 py-0.5 text-[10px] text-slate-600 hover:bg-slate-300">Make active</button>
                <button type="button" title="Delete" onClick={() => onRemove(d.file_id, type)} data-testid={`ca-delete-${type}-${d.file_id}`}
                        className="p-0.5 hover:text-red-500"><X className="h-3 w-3" /></button>
              </div>))}
          </div>)}
      </PopoverContent>
    </Popover>
  );
}

/** Side panel next to the tabs: PDF pages scroll in the browser viewer; DOCX shows its extracted text. */
const currentOf = (docs) => docs.find((d) => d.is_active) || docs[docs.length - 1] || null;
export { currentOf };

/** Right pane: drag the left edge to resize (25–95% of the workspace). Contract | Proposal switch when both exist. */
export function DocViewer({ doc, onClose, width, onWidth, available = {}, onSwitch }) {
  const startDrag = (e) => {
    e.preventDefault();
    const host = e.currentTarget.parentElement.parentElement.getBoundingClientRect();
    const move = (ev) => onWidth(Math.min(95, Math.max(25, ((host.right - ev.clientX) / host.width) * 100)));
    const up = () => { window.removeEventListener("mousemove", move); window.removeEventListener("mouseup", up); document.body.style.userSelect = ""; };
    document.body.style.userSelect = "none";
    window.addEventListener("mousemove", move); window.addEventListener("mouseup", up);
  };
  const [state, setState] = useState({ loading: true });
  const [mode, setMode] = useState("pages");   // PDFs: "pages" (browser viewer) or "text" (extracted text)
  const [text, setText] = useState(null);
  const [thumbs, setThumbs] = useState(false);   // page thumbnails pane — hidden by default, "Show pages" opens it
  useEffect(() => {
    if (mode !== "text" || text !== null) return;
    authed(`/api/documents/${doc.file_id}/text`).then((r) => r.json()).then((j) => setText(j.text || "(no text could be read)")).catch(() => setText("(text unavailable)"));
  }, [mode, text, doc.file_id]);
  useEffect(() => {
    let url = null;
    let live = true;
    (async () => {
      try {
        if (doc.filename.toLowerCase().endsWith(".pdf")) {
          const r = await authed(`/api/files/${doc.file_id}/download`);
          if (!r.ok) throw new Error(`HTTP ${r.status}`);
          url = URL.createObjectURL(new Blob([await r.blob()], { type: "application/pdf" }));
          if (live) setState({ url });
        } else {
          const r = await authed(`/api/documents/${doc.file_id}/text`);
          if (!r.ok) throw new Error(`HTTP ${r.status}`);
          const j = await r.json();
          if (live) setState({ text: j.text || "(no text could be read from this file)" });
        }
      } catch (e) { if (live) setState({ error: e.message }); }
    })();
    return () => { live = false; if (url) URL.revokeObjectURL(url); };
  }, [doc.file_id, doc.filename]);

  return (
    <aside className="relative flex min-w-[20rem] shrink-0 flex-col border-l border-slate-200 bg-white" style={{ width: `${width}%` }} data-testid="ca-doc-viewer">
      <div onMouseDown={startDrag} title="Drag to resize" data-testid="ca-doc-viewer-resize"
           className="group absolute inset-y-0 -left-1.5 z-20 flex w-3 cursor-col-resize items-center justify-center">
        <span className="h-10 w-1 rounded-full bg-slate-300 transition-colors group-hover:bg-[#007BFF]" />
      </div>
      <div className="flex items-center gap-2 border-b border-slate-200 px-3 py-2 text-xs">
        {available.contract && available.proposal ? (
          <span className="inline-flex rounded-full bg-slate-100 p-0.5 text-[10px]" data-testid="ca-doc-viewer-switch">
            {["contract", "proposal"].map((t) => (
              <button key={t} type="button" onClick={() => onSwitch(t)} data-testid={`ca-doc-viewer-show-${t}`}
                      className={`rounded-full px-2 py-0.5 capitalize ${doc.type === t ? "bg-white text-slate-900 shadow-sm" : "text-slate-500"}`}>{t}</button>))}
          </span>
        ) : <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-slate-500">{doc.type}</span>}
        <span className="min-w-0 flex-1 truncate font-medium text-slate-800" title={doc.filename}>{doc.filename}</span>
        <span className="text-[10px] text-slate-500" data-testid="ca-doc-viewer-active"><Hint text="Chat answers from every file attached to this review">
          In this review · chat can read it</Hint></span>
        {state.url && (
          <span className="inline-flex rounded-full bg-slate-100 p-0.5 text-[10px]">
            {["pages", "text"].map((m) => (
              <button key={m} type="button" onClick={() => setMode(m)} data-testid={`ca-doc-viewer-mode-${m}`}
                      className={`rounded-full px-2 py-0.5 capitalize ${mode === m ? "bg-white text-slate-900 shadow-sm" : "text-slate-500"}`}>{m}</button>))}
          </span>)}
        {state.url && mode === "pages" && (
          <button type="button" onClick={() => setThumbs((t) => !t)} data-testid="ca-doc-viewer-thumbs"
                  title={thumbs ? "Hide the page thumbnails" : "Show the page thumbnails"}
                  className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] ${thumbs ? "bg-[#007BFF] text-white" : "bg-slate-100 text-slate-600 hover:text-slate-900"}`}>
            <PanelLeft className="h-3 w-3" />{thumbs ? "Hide pages" : "Show pages"}
          </button>)}
        {state.url && <a href={state.url} target="_blank" rel="noreferrer" className="text-[10px] text-slate-500 underline" data-testid="ca-doc-viewer-newtab">New tab</a>}
        <button type="button" onClick={onClose} title="Close" data-testid="ca-doc-viewer-close" className="p-0.5 text-slate-400 hover:text-slate-700"><X className="h-4 w-4" /></button>
      </div>
      <div className="relative flex-1 overflow-auto">
        {state.loading && <div className="p-6 text-xs text-slate-400">Loading…</div>}
        {state.error && <div className="p-6 text-xs text-red-600" data-testid="ca-doc-viewer-error">Could not open this file ({state.error}).</div>}
        {state.url && mode === "pages" && <iframe key={`${thumbs ? "thumbs" : "plain"}-${doc.page || 1}-${doc.jump || 0}`} title={doc.filename}
                                                     src={`${state.url}#${doc.page ? `page=${doc.page}&` : ""}navpanes=${thumbs ? 1 : 0}&pagemode=${thumbs ? "thumbs" : "none"}&view=FitH`} className="absolute inset-0 h-full w-full" data-testid="ca-doc-viewer-pdf" />}
        {state.url && mode === "text" && <pre className="whitespace-pre-wrap p-4 font-sans text-xs leading-relaxed text-slate-700" data-testid="ca-doc-viewer-text">{text ?? "Loading…"}</pre>}
        {state.text && <pre className="whitespace-pre-wrap p-4 font-sans text-xs leading-relaxed text-slate-700" data-testid="ca-doc-viewer-text">{state.text}</pre>}
      </div>
    </aside>
  );
}
